import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { I18nService } from 'i18n';
import { CommunityService } from '../../services/community.service';
import { CommunityProfileService } from '../../services/community-profile.service';
import {
  COMMUNITY_VISIBILITIES,
  CommunityOwnProfile,
  CommunityProfileUpdate,
  CommunityVisibility
} from '../../models/community-profile.models';
import { CommunityBlockedUser } from '../../models/community-profile.models';
import { communityErrorKey } from '../../models/community.models';
import { CommunityAvatarComponent } from '../community-avatar/community-avatar.component';

/** Contact fields whose value the member edits here (the rest — email/phone/country/city — only get a visibility choice). */
const EDITABLE_FIELDS = new Set(['whatsapp', 'website', 'social']);

/**
 * The member's own Community Network profile (§6/§7, Phase 3): avatar, bio,
 * the practitioner switches, and every contact field with its own
 * private/network/groups visibility — embedded as a tab in the app's regular
 * profile page (shared by both apps, so it lives here rather than in credo).
 *
 * Renders nothing (not even a spinner) until `load()` is called — the host
 * decides when the tab actually needs the data, since it may not be the tab
 * the member opens first.
 */
@Component({
  selector: 'app-community-profile-section',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, TranslocoModule, CommunityAvatarComponent],
  templateUrl: './community-profile-section.component.html',
  styleUrl: './community-profile-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityProfileSectionComponent implements OnInit {
  private community = inject(CommunityService);
  private profileService = inject(CommunityProfileService);
  private i18n = inject(I18nService);

  readonly visibilities = COMMUNITY_VISIBILITIES;
  readonly editableFields = EDITABLE_FIELDS;

  profile = signal<CommunityOwnProfile | null>(null);
  loading = signal(true);
  saving = signal(false);
  error = signal<string | null>(null);
  saved = signal(false);

  avatarBusy = signal(false);
  avatarError = signal<string | null>(null);

  blocked = signal<CommunityBlockedUser[]>([]);
  blockedLoading = signal(false);
  blockBusy = signal<number | null>(null);

  // Draft fields, populated once the profile arrives.
  bio = '';
  calling = '';
  whatsapp = '';
  website = '';
  social: Record<string, string> = {};
  visibility: Record<string, CommunityVisibility> = {};
  practitionerVisible = false;
  practitionerAcceptsDm = false;
  /** "Profiles connect people": drafts of the lists as comma-separated text. */
  lists: Record<string, string> = {};
  countryCode = '';
  region = '';
  details: Record<string, string> = {};

  /** The list fields the backend knows (hobbies, interests, exchange_topics). */
  listFields(): string[] {
    return this.profile()?.list_fields ?? ['hobbies', 'interests', 'exchange_topics'];
  }

  detailKeys(): string[] {
    return this.profile()?.practitioner_detail_keys ?? [];
  }

  listValue(field: string): string {
    return this.lists[field] ?? '';
  }

  setListValue(field: string, value: string): void {
    this.lists = { ...this.lists, [field]: value };
  }

  detailValue(key: string): string {
    return this.details[key] ?? '';
  }

  setDetailValue(key: string, value: string): void {
    this.details = { ...this.details, [key]: value };
  }

  ngOnInit(): void {
    this.load();
    this.loadBlocked();
  }

  get lang(): string {
    return this.i18n.getLanguage();
  }

  memberLink(userId: number): string {
    return `/${this.lang}/community/u/${userId}`;
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.profileService.getProfile().subscribe({
      next: res => {
        this.applyProfile(res.profile);
        this.loading.set(false);
      },
      error: err => {
        this.loading.set(false);
        this.error.set(communityErrorKey(err));
      }
    });
  }

  private applyProfile(profile: CommunityOwnProfile): void {
    this.profile.set(profile);
    this.bio = profile.bio ?? '';
    this.calling = profile.calling ?? '';
    this.whatsapp = profile.whatsapp ?? '';
    this.website = profile.website ?? '';
    this.social = { ...profile.social };
    this.visibility = { ...profile.visibility };
    this.practitionerVisible = profile.practitioner_visible;
    this.practitionerAcceptsDm = profile.practitioner_accepts_dm;
    this.lists = Object.fromEntries(this.listFields().map(f => [f, ((profile as unknown as Record<string, string[]>)[f] ?? []).join(', ')]));
    this.countryCode = profile.country_code ?? '';
    this.region = profile.region ?? '';
    this.details = { ...(profile.practitioner_details ?? {}) };
  }

  visibilityOf(field: string): CommunityVisibility {
    return this.visibility[field] ?? 'private';
  }

  /** Current draft value of one social network; a network the member never filled in is simply absent from the map. */
  socialValue(key: string): string {
    return this.social[key] ?? '';
  }

  setSocialValue(key: string, value: string): void {
    this.social = { ...this.social, [key]: value };
  }

  setVisibility(field: string, value: CommunityVisibility): void {
    this.visibility = { ...this.visibility, [field]: value };
  }

  /** Current draft value of an editable contact field (whatsapp/website). */
  editableValue(field: string): string {
    return field === 'whatsapp' ? this.whatsapp : this.website;
  }

  setEditableValue(field: string, value: string): void {
    if (field === 'whatsapp') this.whatsapp = value;
    else this.website = value;
  }

  /** Read-only display value of a non-editable contact field (email/phone/country/city). */
  fieldValue(field: string): string {
    const u = this.profile()?.user;
    if (!u) return '';
    switch (field) {
      case 'email': return u.email ?? '';
      case 'phone': return u.phone ?? '';
      case 'country': return u.country ?? '';
      case 'city': return u.city ?? '';
      default: return '';
    }
  }

  save(): void {
    const profile = this.profile();
    if (!profile) return;
    const payload: CommunityProfileUpdate = {
      bio: this.bio.trim() || null,
      whatsapp: this.whatsapp.trim() || null,
      website: this.website.trim() || null,
      social: this.social,
      visibility: this.visibility,
      country_code: this.countryCode.trim() || null,
      region: this.region.trim() || null
    };
    this.listFields().forEach(f => {
      (payload as unknown as Record<string, string[]>)[f] = (this.lists[f] ?? '').split(',').map(v => v.trim()).filter(Boolean);
    });
    if (profile.is_practitioner) {
      payload.calling = this.calling.trim() || null;
      payload.practitioner_visible = this.practitionerVisible;
      payload.practitioner_accepts_dm = this.practitionerAcceptsDm;
      payload.practitioner_details = this.details;
    }
    this.saving.set(true);
    this.error.set(null);
    this.saved.set(false);
    this.profileService.updateProfile(payload).subscribe({
      next: res => {
        this.saving.set(false);
        this.applyProfile(res.profile);
        this.saved.set(true);
        setTimeout(() => this.saved.set(false), 4000);
      },
      error: err => {
        this.saving.set(false);
        this.error.set(communityErrorKey(err));
      }
    });
  }

  onAvatarSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (file.size > CommunityProfileService.AVATAR_MAX_BYTES) {
      this.avatarError.set('community.errors.avatar_too_large');
      return;
    }
    if (!CommunityProfileService.AVATAR_TYPES.includes(file.type)) {
      this.avatarError.set('community.errors.avatar_invalid');
      return;
    }
    this.avatarBusy.set(true);
    this.avatarError.set(null);
    this.profileService.uploadAvatar(file).subscribe({
      next: res => {
        this.avatarBusy.set(false);
        this.profile.update(p => (p ? { ...p, user: { ...p.user, avatar_url: res.avatar_url } } : p));
      },
      error: err => {
        this.avatarBusy.set(false);
        this.avatarError.set(communityErrorKey(err));
      }
    });
  }

  removeAvatar(): void {
    this.avatarBusy.set(true);
    this.avatarError.set(null);
    this.profileService.deleteAvatar().subscribe({
      next: () => {
        this.avatarBusy.set(false);
        this.profile.update(p => (p ? { ...p, user: { ...p.user, avatar_url: null } } : p));
      },
      error: err => {
        this.avatarBusy.set(false);
        this.avatarError.set(communityErrorKey(err));
      }
    });
  }

  loadBlocked(): void {
    this.blockedLoading.set(true);
    this.community.listBlocks().subscribe({
      next: res => {
        this.blocked.set(res.data);
        this.blockedLoading.set(false);
      },
      error: () => this.blockedLoading.set(false)
    });
  }

  unblock(user: CommunityBlockedUser): void {
    this.blockBusy.set(user.id);
    this.community.unblockUser(user.id).subscribe({
      next: () => {
        this.blockBusy.set(null);
        this.blocked.update(list => list.filter(u => u.id !== user.id));
      },
      error: () => this.blockBusy.set(null)
    });
  }
}
